import { create } from 'zustand';
import { api } from '../lib/api';
import type { Location, Operator, Role, SessionUser } from '../../../shared/contracts';

export type TabKey = 'scan' | 'inventory' | 'pallets' | 'packing' | 'requests' | 'projects' | 'activity' | 'config';

interface StoreState {
	user: SessionUser | null;
	loadingUser: boolean;
	locations: Location[];
	operators: Operator[];
	/** Cross-tab navigation: when set, AppShell switches to that tab. */
	bootstrap: () => Promise<void>;
	login: (username: string, password: string) => Promise<void>;
	logout: () => Promise<void>;
	loadReference: () => Promise<void>;
	refreshLocations: () => Promise<void>;
	refreshOperators: () => Promise<void>;
}

export const useStore = create<StoreState>((set, get) => ({
	user: null,
	loadingUser: true,
	locations: [],
	operators: [],

	async bootstrap() {
		set({ loadingUser: true });
		try {
			const { user } = await api.me();
			set({ user });
			if (user) await get().loadReference();
		} finally {
			set({ loadingUser: false });
		}
	},

	async login(username, password) {
		const user = await api.login({ username, password });
		set({ user });
		await get().loadReference();
	},

	async logout() {
		await api.logout().catch(() => undefined);
		set({ user: null, locations: [], operators: [] });
	},

	async loadReference() {
		const locations = await api.listLocations();
		set({ locations });
		if (get().user?.roles.includes('admin')) {
			set({ operators: await api.listOperators() });
		}
	},

	async refreshLocations() {
		set({ locations: await api.listLocations() });
	},

	async refreshOperators() {
		set({ operators: await api.listOperators() });
	},
}));

// Imperative access (mirrors the old singleton `store` object for non-component code).
export const store = useStore;

function hasRole(user: SessionUser | null, role: Role): boolean {
	if (!user) return false;
	if (user.roles.includes('admin')) return true;
	return user.roles.includes(role);
}

// Role selector hooks.
export const useIsAdmin = () => useStore((s) => s.user?.roles.includes('admin') ?? false);
export const useIsYard = () => useStore((s) => hasRole(s.user, 'yard'));
export const useIsPacker = () => useStore((s) => hasRole(s.user, 'packer'));
export const useIsPM = () => useStore((s) => hasRole(s.user, 'pm'));
export const useIsDelivery = () => useStore((s) => hasRole(s.user, 'delivery'));
